import {Inject, Injectable} from '@angular/core';
import {Box3, BufferGeometry, Geometry, Matrix4, Mesh, Object3D, Scene, Vector3} from 'three';
import {WebWorkerService} from 'ngx-web-worker';

@Injectable({
    providedIn: 'root'
})
export class ObjectUtilsService {

    constructor(@Inject(WebWorkerService) private _webWorkerService: WebWorkerService) {

    }

    /**
     * scales mesh to best fit view
     * @param object
     */
    public scaleMesh(object: Mesh): number {
        const box = new Box3().setFromObject(object);
        const scaleFactor = (box.max.z - box.min.z) > 2 ? 15 : 150;
        object.scale.set(scaleFactor, scaleFactor, scaleFactor);
        object.position.set(0, 0, 0);
        object.updateMatrix();
        return scaleFactor;
    }

    /**
     * merges vertices of two meshes
     * @param m1: 1st mesh
     * @param m2: 2nd mesh
     * @returns BufferGeometry of merged vertices
     */
    public mergeMeshes(m1: Mesh, m2: Mesh): BufferGeometry {
        const geo = new Geometry();

        m1.updateMatrix();
        geo.merge(new Geometry().fromBufferGeometry(<BufferGeometry>m1.geometry), m1.matrix);

        m2.updateMatrix();
        geo.merge(new Geometry().fromBufferGeometry(<BufferGeometry>m2.geometry), m2.matrix);

        return new BufferGeometry().fromGeometry(geo);
    }

    /**
     * rotates mesh in x, y, z, direction
     */
    public rotateBy(mesh: Mesh, x: number, y: number, z: number) {
        mesh.rotateX(x);
        mesh.rotateY(y);
        mesh.rotateZ(z);
    }

    /**
     * moves mesh to position (x, y, z)
     */
    public moveTo(mesh: Mesh, x: number, y: number, z: number) {
        mesh.position.x = x;
        mesh.position.y = y;
        mesh.position.z = z;
    }

    /**
     * returns child in scene with given name
     * @param scene: scene graph containing objects
     * @param name: name of mesh in scene
     */
    public getMeshWithName(scene: any, name: string): Mesh {
        for (let i = 0; i < scene.children.length; ++i) {
            const child = scene.children[i];
            if (child.name === name) {
                return <Mesh>child;
            }
        }
        return null;
    }

    /**
     * computes normals and centers geometry
     */
    public normalizeGeometry(geometry) {
        geometry.computeFaceNormals();
        geometry.computeVertexNormals(true);
        geometry.center();

        geometry.verticesNeedUpdate = true;
        geometry.elementsNeedUpdate = true;
    }

    /**
     * sets all morphTargetInfluences of model to 0
     * @param: model: mesh to reset influences of
     * @param length: length of morphTargetInfluences
     */
    public resetTargetInfluences(model: Mesh, length) {
        model.morphTargetInfluences = new Array<number>(length);
        for (let i = 0; i < model.morphTargetInfluences.length; ++i) {
            model.morphTargetInfluences[i] = 0;
        }
    }

    /**
     * computes targets
     * @returns array of targets
     * @param bufferGeometry
     * @param geometry
     */
    public computeMorphTargets(bufferGeometry: BufferGeometry, geometry) {
        const morphAttributes = bufferGeometry.morphAttributes;

        const targets = [];
        const targetInfluences = [];
        const arrayTargets = [];
        const position = morphAttributes.position;

        if (position) {
            for (let i = 0; i < position.length; i++) {
                const buffer = position[i];

                const vertices = [];
                const arrayVertices = [];

                for (let v = 0; v < geometry.vertices.length; v++) {
                    vertices.push(geometry.vertices[v].clone());
                    vertices[vertices.length - 1].x = buffer.getX(v);
                    vertices[vertices.length - 1].y = buffer.getY(v);
                    vertices[vertices.length - 1].z = buffer.getZ(v);

                    arrayVertices.push(vertices[vertices.length - 1].toArray());
                }
                targets.push({name: 'target' + i, vertices: vertices});
                arrayTargets.push(arrayVertices);
                targetInfluences.push(0);
            }
        }

        return {targets: targets, arrayTargets: arrayTargets, influences: targetInfluences};
    }

    public updateVertices(meshes: Array<Mesh>, targets, influences: Array<number>) {
        const vertices = (<Geometry> meshes[0].geometry).vertices;

        // iterate over all vertices
        for (let i = 0; i < vertices.length; ++i) {
            const vertex = vertices[i];

            let morphedVertex = new Vector3();
            // iterate over all morph targets and compute the morphed coordinates
            for (let j = 0; j < influences.length; ++j) {
                const morphVertex = targets[j].vertices[i];
                const influence = influences[j];

                if (influence !== 0) {
                    morphedVertex = morphedVertex.addScaledVector(new Vector3().subVectors(morphVertex, vertex), influence);
                }
            }

            const newVertex = vertex.add(morphedVertex);
            for (let m = 0; m < meshes.length; ++m) {
                const meshVertices = (<Geometry> meshes[m].geometry).vertices;
                meshVertices[i] = newVertex.clone();
            }

        }

        // merge vertices AFTER! updating vertices
        for (let m = 0; m < meshes.length; ++m) {
            const geometry = <Geometry> meshes[m].geometry;
            geometry.mergeVertices();
            this.normalizeGeometry(geometry);
        }

    }

    /**
     * computes neighbouring faces for each face in geometry
     * uses web worker for background computation
     * @returns promise of web worker task
     */
    public computeFaceNeighbours(geometry: Geometry): Promise<any> {
        const promise = this._webWorkerService.run((geo) => {
            const faceNeighbours = new Map<number, Array<number>>();

            for (let i = 0; i < geo.faces.length; ++i) {
                const face = geo.faces[i];
                const neighbours = [];
                for (let j = 0; j < geo.faces.length; ++j) {
                    const f = geo.faces[j];
                    if (face !== f && (f.a === face.a || f.a === face.b || f.a === face.c ||
                                       f.b === face.a || f.b === face.b || f.b === face.c ||
                                       f.c === face.a || f.c === face.b || f.c === face.c)) {
                        neighbours.push(j);
                    }
                }
                faceNeighbours.set(i, neighbours);
            }

            return faceNeighbours;
        }, geometry);

        return promise;
    }

    /**
     * computes how much each target offsets the model from the center
     * uses web worker for background computation
     * @returns promise of web worker task
     */
    public computeTargetOffsets(targets: Array<any>, model: Mesh): Promise<any> {
        const vertices = (<Geometry>model.geometry).vertices.map(vertex => vertex.toArray());
        const promise = this._webWorkerService.run((input) => {
            const targets_help = input.targets;
            const v = input.vertices;

            const offsets = [];
            targets_help.forEach((t) => {
                let x = 0;
                let y = 0;
                let z = 0;

                // add up vertex offsets for targets
                for (let i = 0; i < t.length; ++i) {
                    y = ((t[i][1] - v[i][1] + y));
                    z = ((t[i][2] - v[i][2] + z));
                    x = ((t[i][0] - v[i][0] + x));
                }

                // get average offset for target
                x /= t.length;
                y /= t.length;
                z /= t.length;

                offsets.push([x, y, z]);
            });

            return offsets;

        }, {
            targets: targets,
            vertices: vertices
        });

        return promise;
    }

    public resetRotation(mesh: Object3D) {
        mesh.setRotationFromMatrix(new Matrix4());
    }

    // -------------------------------------------------------------------------------------------------------------------------------------

}
